import { CommentApi } from "@/config/cms/api";
import Cookies from "js-cookie";
import _ from "lodash";
import React from "react";

export function useCommentList({ fetchDelay = 0, contentKey, userKey } = {}) {
  const [items, setItems] = React.useState([]);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const limit = 10; // Number of items per page, adjust as necessary

  const loadComment = async (page) => {
    var undf = undefined;
    const controller = new AbortController();
    const { signal } = controller;

    try {
      setIsLoading(true);

      if (page > 1) {
        await new Promise((resolve) => setTimeout(resolve, fetchDelay));
      }

      const response = await CommentApi.prototype.commentControllerGetComments(
        page,
        limit,
        contentKey,
        userKey && userKey,
        1,
        undf,
        {
          signal,
          headers: {
            Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
          },
        }
      );

      if (!response.statusText === "ok") {
        throw new Error("Network response was not ok");
      }

      const { data } = response.data;

      setHasMore(data?.total > page * limit);
      setItems((prevItems) =>
        _.uniqBy([...prevItems, ...data?.results], (x) => x._id)
      );
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("There was an error with the fetch operation:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const reloadComments = async () => {
    var undf = undefined;
    const controller = new AbortController();
    const { signal } = controller;

    try {
      setIsLoading(true);

      if (page > 1) {
        await new Promise((resolve) => setTimeout(resolve, fetchDelay));
      }

      const response = await CommentApi.prototype.commentControllerGetComments(
        1,
        limit * page,
        contentKey,
        userKey && userKey,
        1,
        undf,
        {
          signal,
          headers: {
            Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
          },
        }
      );

      if (!response.statusText === "ok") {
        throw new Error("Network response was not ok");
      }

      const { data } = response.data;

      setHasMore(data?.total > page * limit);
      setItems((prevItems) =>
        _.uniqBy([...prevItems, ...data?.results], (x) => x._id)
      );
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("There was an error with the fetch operation:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const reloadCommentsSilent = async () => {
    var undf = undefined;
    const controller = new AbortController();
    const { signal } = controller;

    try {
      const response = await CommentApi.prototype.commentControllerGetComments(
        page,
        limit * page,
        contentKey,
        userKey && userKey,
        1,
        undf,
        {
          signal,
          headers: {
            Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
          },
        }
      );

      if (!response.statusText === "ok") {
        throw new Error("Network response was not ok");
      }

      const { data } = response.data;

      setHasMore(data?.total > page * limit);
      setItems(
        _.cloneDeep(_.uniqBy([...items, ...data?.results], (x) => x._id))
      );
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("There was an error with the fetch operation:", error);
      }
    }
  };

  React.useEffect(() => {
    loadComment(page);
  }, []);

  const onLoadMore = () => {
    const newOffset = page + 1;

    setPage(newOffset);
    loadComment(newOffset);
  };

  return {
    items,
    hasMore,
    isLoading,
    onLoadMore,
    reloadComments,
    reloadCommentsSilent,
  };
}
